import React, { useState } from 'react';
import { Card, Button, Form, Dimmer, Grid } from "tabler-react";
import { useFetch } from '../../lib/useFetch';
import PutObject from '../../lib/putObject';
import ImageUpload from '../../components/imageUpload';

const CountryEditModal = ({ recordId, closeMainModal, reload }) => {
  const { data: country, loading: countryLoading, handleChange } = useFetch(`countries/${recordId}`);
  const [saveLoading, setSaveLoading] = useState(false);
  const [error, setError] = useState('');
  const [flagChange, setFlagChange] = useState(null);

  const validateForm = () => {
    if (!country?.name) {
      setError('Please enter country name');
      return false;
    }
    if (!country?.countryCode) {
      setError('Please enter country code');
      return false;
    }
    if (!country?.currency) {
      setError('Please enter currency');
      return false;
    }
    if (!country?.pricePerKg && country?.pricePerKg !== 0) {
      setError('Please enter price per kg');
      return false;
    }
    if (!country?.minTravelerReward && country?.minTravelerReward !== 0) {
      setError('Please enter minimum traveler reward');
      return false;
    }
    if (!country?.moveitFeesPercentageOfTheTravelerReward && country?.moveitFeesPercentageOfTheTravelerReward !== 0) {
      setError('Please enter fees reward percentage');
      return false;
    }
    return true;
  };

  const saveEdit = async () => {
    setError('');
    if (!validateForm()) return;

    try {
      setSaveLoading(true);

      const editedCountry = {
        _id: recordId,
        name: country.name,
        countryCode: country.countryCode,
        currency: country.currency,
        pricePerKg: country.pricePerKg,
        minTravelerReward: country.minTravelerReward,
        moveitFeesPercentageOfTheTravelerReward: country.moveitFeesPercentageOfTheTravelerReward,
      };

      if (flagChange !== null) {
        editedCountry.countryFlag = flagChange;
      }

      await PutObject('countries', editedCountry);

      if (typeof reload === 'function') {
        reload();
      }
      closeMainModal();
    } catch (err) {
      setError('Failed to update country. Please try again.');
    } finally {
      setSaveLoading(false);
    }
  };

  return (
    <Card>
      <Dimmer active={countryLoading} loader={countryLoading}>
        <Card.Header>
          <Card.Title>Edit Country</Card.Title>
          <Card.Options>
            <Button size="sm" icon="x" onClick={closeMainModal} />
          </Card.Options>
        </Card.Header>
        {!countryLoading && country && (
          <>
            <Card.Body>
              {error && <div className="alert alert-danger">{error}</div>}
              <Form onSubmit={(e) => { e.preventDefault(); saveEdit(); }}>
                <Form.FieldSet>
                  <Grid>
                    <Grid.Row>
                      <Grid.Col width={6}>
                        <ImageUpload
                          label="Country Flag Photo"
                          initialUrl={country?.countryFlag}
                          uploadText="Click here to upload country flag"
                          alt="Country Flag Preview"
                          onChange={(value) => setFlagChange(value)}
                        />

                        <Form.Input
                          name='countryCode'
                          label='Country Code'
                          placeholder='Enter country code (ex. US, EG, SA)'
                          value={country['countryCode'] || ''}
                          onChange={handleChange}
                          required
                        />

                        <Form.Input
                          name='currency'
                          label='Currency'
                          placeholder='Enter currency (ex. USD, EGP, SAR)'
                          value={country['currency'] || ''}
                          onChange={handleChange}
                          required
                        />

                        <Form.Input
                          name='moveitFeesPercentageOfTheTravelerReward'
                          label='Fees Percentage'
                          type="number"
                          min="0"
                          max="100"
                          step="0.01"
                          placeholder='Enter fees reward percentage'
                          value={country['moveitFeesPercentageOfTheTravelerReward'] || ''}
                          onChange={handleChange}
                          required
                        />
                      </Grid.Col>

                      <Grid.Col width={6}>
                        <Form.Input
                          name='name'
                          label='Name'
                          placeholder='Enter country name'
                          value={country['name'] || ''}
                          onChange={handleChange}
                          required
                        />

                        <Form.Input
                          name='pricePerKg'
                          label='Price Per Kg'
                          type="number"
                          min="0"
                          step="0.01"
                          placeholder='Enter price per kilogram'
                          value={country['pricePerKg'] || ''}
                          onChange={handleChange}
                          required
                        />

                        <Form.Input
                          name='minTravelerReward'
                          label='Min Reward'
                          type="number"
                          min="0"
                          step="0.01"
                          placeholder='Enter minimum traveler reward'
                          value={country['minTravelerReward'] || ''}
                          onChange={handleChange}
                          required
                        />
                      </Grid.Col>
                    </Grid.Row>
                  </Grid>
                </Form.FieldSet>
              </Form>
            </Card.Body>
            <Card.Footer>
              <div style={{ textAlign: 'right' }}>
                <Button
                  color="primary"
                  size="sm"
                  className={`ml-2 ${saveLoading ? 'btn-loading' : ''}`}
                  onClick={saveEdit}
                  disabled={saveLoading}
                >
                  Save Changes
                </Button>
                <Button
                  color="secondary"
                  size="sm"
                  className="ml-2"
                  onClick={closeMainModal}
                  disabled={saveLoading}
                >
                  Cancel
                </Button>
              </div>
            </Card.Footer>
          </>
        )}
      </Dimmer>
    </Card>
  );
};

export default CountryEditModal;
